/** Account (Earned Revenue) payout eligibility — GET /account/payout-info */

export type AccountCashoutTier = 'instant' | 'blocked' | 'delayed' | string;
export type AccountPayoutBlockReason =
    | 'awaiting_scheduled_payout'
    | 'below_minimum'
    | 'pending_payout'
    | 'plan_not_eligible'
    | 'missing_external_account'
    | string;

export interface IAccountPayoutInfo {
    balance_cents: number;
    balance?: number;
    /** Office-hours earnings held until after session + hold days */
    pending_balance_cents?: number;
    pending_balance?: number;
    office_hours_hold_days?: number;
    currency?: string;
    minimum_withdrawal_cents: number;
    meets_minimum: boolean;
    plan_class?: string | null;
    cashout_tier?: AccountCashoutTier;
    payout_timing?: 'immediate' | 'scheduled' | 'blocked' | string;
    has_active_subscription?: boolean;
    has_external_account?: boolean;
    full_balance_only?: boolean;
    can_request_now: boolean;
    withdrawal_enabled: boolean;
    block_reason?: AccountPayoutBlockReason | null;
    pending_payout?: {
        id?: string;
        amount_cents?: number;
        created_at?: string;
        scheduled_for?: string | null;
    } | null;
    next_scheduled_payout_at?: string | null;
    next_scheduled_payout_timezone?: string;
    delayed_working_days?: number;
    schedule?: {
        cadence: string;
        day: string;
        local_time: string;
        timezone: string;
        cron: string;
    };
    auto_transfer_enabled?: boolean;
    message?: string | null;
}

export function normalizeAccountPayoutInfo(raw: any): IAccountPayoutInfo {
    const payout = raw?.payout ?? {};
    const balanceCents = raw?.balance_cents ?? payout.balance_cents ?? 0;
    const minCents = raw?.minimum_withdrawal_cents ?? payout.minimum_withdrawal_cents ?? 4000;
    const canRequest = Boolean(payout.can_request_now ?? raw?.can_request_now ?? false);
    const withdrawalEnabled = Boolean(payout.withdrawal_enabled ?? raw?.withdrawal_enabled ?? canRequest);

    return {
        balance_cents: balanceCents,
        balance: raw?.balance ?? balanceCents / 100,
        pending_balance_cents: Number(raw?.pending_balance_cents ?? 0) || 0,
        pending_balance: raw?.pending_balance ?? (Number(raw?.pending_balance_cents ?? 0) || 0) / 100,
        office_hours_hold_days: Number(raw?.office_hours_hold_days ?? 7) || 7,
        currency: raw?.currency ?? 'USD',
        minimum_withdrawal_cents: minCents,
        meets_minimum: payout.meets_minimum ?? raw?.meets_minimum ?? balanceCents >= minCents,
        plan_class: payout.plan_class ?? raw?.plan_class ?? null,
        cashout_tier: payout.cashout_tier ?? raw?.cashout_tier,
        payout_timing: payout.payout_timing ?? raw?.payout_timing,
        has_active_subscription: payout.has_active_subscription ?? raw?.has_active_subscription ?? false,
        has_external_account: payout.has_external_account ?? raw?.has_external_account ?? false,
        full_balance_only: payout.full_balance_only ?? raw?.full_balance_only ?? false,
        can_request_now: canRequest,
        withdrawal_enabled: withdrawalEnabled,
        block_reason: payout.block_reason ?? raw?.block_reason ?? null,
        pending_payout: payout.pending_payout ?? raw?.pending_payout ?? null,
        next_scheduled_payout_at: payout.next_scheduled_payout_at ?? raw?.next_scheduled_payout_at ?? null,
        next_scheduled_payout_timezone:
            payout.next_scheduled_payout_timezone ??
            raw?.next_scheduled_payout_timezone ??
            payout.schedule?.timezone ??
            'America/New_York',
        delayed_working_days: payout.delayed_working_days ?? raw?.delayed_working_days ?? 14,
        schedule: payout.schedule ?? raw?.schedule,
        auto_transfer_enabled: payout.auto_transfer_enabled ?? raw?.auto_transfer_enabled ?? false,
        message: payout.message ?? raw?.message,
    };
}
